import { useState } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { createForm } from '@components/form';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { useResetPassword } from 'api/users/hooks';

import { Subtitle, Title } from './common';

import { routePaths } from '../navigation';

export type ForgotEmailData = {
  email: string;
};

const Form = createForm<ForgotEmailData>();

const ForgotPasswordForm = (props: { setEmail: (email: string) => void }) => {
  const { t } = useScopedTranslate(locale);

  const resetPasswordMutation = useResetPassword();

  const handleSave = (data: ForgotEmailData) => {
    resetPasswordMutation.mutate({
      email: data.email,
    });
    props.setEmail(data.email);
  };
  return (
    <Stack space={6}>
      <Stack align="center" space={1}>
        <Title>{t('forgotYourPassword')}</Title>
        <Subtitle>{t('enterEmail')}</Subtitle>
      </Stack>
      <Form onSubmit={(data) => handleSave(data)}>
        <Form.TextField label={t('email')} id="email" validators={['required', 'email']} />
        <Form.Submit text={t('resetPassword')} variant="primary-blue" />
      </Form>
      <ActionText.Link
        to={`/${routePaths.signin}`}
        children={t('backToSignin')}
        font="regular14"
        color="textHelper"
        center
      />
    </Stack>
  );
};
const FormSubmitted = (props: { email: string }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={6}>
      <Stack align="center">
        <Title>{t('checkEmail')}</Title>
        <Subtitle>
          {t('registered')} <BlueText>{props.email}</BlueText>
        </Subtitle>
      </Stack>
      <Subtitle>{t('unregistered')}</Subtitle>
      <ActionText.Link
        to={`/${routePaths.signin}`}
        children={t('backToSignin')}
        font="regular14"
        color="textHelper"
        center
      />
    </Stack>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');

  return email.length > 0 ? (
    <FormSubmitted email={email} />
  ) : (
    <ForgotPasswordForm setEmail={setEmail} />
  );
};

const BlueText = styled.span({
  color: theme.color.elementBlue100,
});

const locale = {
  en: {
    forgotYourPassword: 'Forgot your password?',
    enterEmail:
      "Enter your email address and we'll send you instructions for resetting your password",
    email: 'Email',
    resetPassword: 'Reset password',
    backToSignin: 'Back to signin page',
    checkEmail: 'Check your email',
    registered:
      'If you have a registered account, you should receive an email with instructions on how to reset your password shortly at',
    unregistered:
      'If you do not receive an email within the next 5 minutes, please check your spam folder, contact support or an administrator at your company',
  },
};
